import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import BackgroundBlock from "./BackgroundBlock";
import CourseCard from "./CourseCard";
import CourseForm from "./CourseForm";
import { addCourseToFirestore, updateCourseInFirestore, deleteCourseFromFirestore } from '../../admin';
import { debounce } from '../../tool';
import { HomeHeader } from "./HomeHeader";
import AdminDataService from "../../services/AdminDataService";
import dayjs from 'dayjs';
import { hasCourseEnded } from '../../utils/courseSchedule';
import { MdPlayCircleFilled, MdArchive } from 'react-icons/md';
import { BsGripVertical } from 'react-icons/bs';
import { FiAlertCircle, FiSend } from 'react-icons/fi';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';

const toDayjs = (d) => {
    if (!d) return null;
    if (typeof d?.toDate === 'function') return dayjs(d.toDate()); // Firestore Timestamp
    return dayjs(d); // Date or ISO string
};

const AdminHome = () => {
    const [courses, setCourses] = useState([]);
    const [addingCourse, setAddingCourse] = useState(false);
    const [newCourse, setNewCourse] = useState({ name: "", description: "", startDate: null, endDate: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isProjectManager, setIsProjectManager] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin } = useAuth();
    const isInitialMount = useRef(true);
    const prevLocationKey = useRef(location.key);

    const loadCourses = useCallback(async (forceRefresh = false) => {
        if (!user || !user.email) return;

        setLoading(true);
        try {
            const loadedCourses = await AdminDataService.getCourses(user.email, forceRefresh);
            setCourses(loadedCourses);
        } catch (error) {
            console.error("Error loading courses: ", error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        const loadReleaseNotesPermission = async () => {
            if (!user?.email) return;

            try {
                const snap = await getDoc(doc(db, "Users", user.email));
                setIsProjectManager(snap.exists() && snap.data()?.isProjectManager === true);
            } catch (error) {
                console.error("Error loading release notes permission:", error);
                setIsProjectManager(false);
            }
        };

        loadReleaseNotesPermission();
    }, [user?.email]);

    useEffect(() => {
        // Check if course status was changed while away from this page
        const statusChangedTimestamp = sessionStorage.getItem('courseStatusChanged');
        const shouldRefresh = statusChangedTimestamp && 
            (!isInitialMount.current || 
             Date.now() - parseInt(statusChangedTimestamp) < 60000); // Refresh if changed within last minute
        
        if (shouldRefresh) {
            sessionStorage.removeItem('courseStatusChanged');
            AdminDataService.clearCache('courses');
            loadCourses(true);
        } else {
            loadCourses();
        }
        
        isInitialMount.current = false;
        prevLocationKey.current = location.key;
    }, [loadCourses]);

    // Refresh courses when returning to home page (detected by location key change)
    useEffect(() => {
        // Only refresh if we're navigating back to home from another page (not on initial mount)
        if (!isInitialMount.current && 
            location.pathname === '/admin/home' && 
            prevLocationKey.current !== location.key) {
            // Clear cache and reload courses when returning to home
            AdminDataService.clearCache('courses');
            loadCourses(true);
        }
        prevLocationKey.current = location.key;
    }, [location.key, location.pathname, loadCourses]);

    // Listen for course active status changes (when course is deactivated/activated in group manager)
    useEffect(() => {
        const handleCourseStatusChange = () => {
            if (location.pathname === '/admin/home') {
                AdminDataService.clearCache('courses');
                loadCourses(true);
            }
        };

        window.addEventListener('courseActiveStatusChanged', handleCourseStatusChange);

        return () => {
            window.removeEventListener('courseActiveStatusChanged', handleCourseStatusChange);
        };
    }, [location.pathname, loadCourses]);

    // Separate active vs inactive courses - memoized to prevent unnecessary re-renders
    // Active courses: isActive is true (or undefined for backward compatibility), not archived, and end time is in future or null
    const allActiveCourses = useMemo(() => 
        courses.filter(c => {
            const isActive = c.isActive !== false; // Treat undefined as active for backward compatibility
            const notArchived = !c.isArchived;
            return isActive && notArchived && !hasCourseEnded(c);
        }),
        [courses]
    );
    // Inactive courses: isActive is false, or archived, or end time has passed
    const allInactiveCourses = useMemo(() => 
        courses.filter(c => {
            const isInactive = c.isActive === false;
            const isArchived = c.isArchived === true;
            return isInactive || isArchived || hasCourseEnded(c);
        }),
        [courses]
    );

    // Memoize storage keys to prevent hook re-initialization
    const activeStorageKey = useMemo(() => 
        `courseOrder:active:${user?.email || 'default'}`,
        [user?.email]
    );
    const inactiveStorageKey = useMemo(() => 
        `courseOrder:inactive:${user?.email || 'default'}`,
        [user?.email]
    );

    // Use drag and drop hooks for both sections
    const activeDragDrop = useDragAndDrop(allActiveCourses, activeStorageKey);
    const inactiveDragDrop = useDragAndDrop(allInactiveCourses, inactiveStorageKey);

    const activeCourses = activeDragDrop.orderedItems;
    const inactiveCourses = inactiveDragDrop.orderedItems;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCourse((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (field, value) => {
        setNewCourse((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddCourse = async () => {
        if (isSubmitting) return;
        if (!newCourse.name || !newCourse.startDate) {
            alert("Please fill in both the course name and start date.");
            return;
        }

        setIsSubmitting(true);

        try {
            const newCourseId = await addCourseToFirestore(newCourse, user.email);
            const updatedCourses = [...courses, { ...newCourse, id: newCourseId }];
            setCourses(updatedCourses);
            setNewCourse({ name: "", description: "", startDate: null, endDate: null });
            setAddingCourse(false);

            // Clear the courses cache after adding a new course
            AdminDataService.clearCache('courses');
            
            // Clear localStorage order to include new course
            const storageKey = `courseOrder:active:${user?.email || 'default'}`;
            localStorage.removeItem(storageKey);
        } catch (error) {
            console.error("Error adding course: ", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const debouncedAddCourse = useCallback(debounce(handleAddCourse, 500), [newCourse, isSubmitting, courses]);

    const handleCancel = () => {
        setShowCancelConfirm(true);
    };

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        setAddingCourse(false);
        setNewCourse({ name: "", description: "", startDate: null, endDate: null });
    };

    const handleUpdate = async (updatedCourse) => {
        try {
            await updateCourseInFirestore(updatedCourse.id, updatedCourse);
            setCourses(courses.map(course =>
                course.id === updatedCourse.id ? updatedCourse : course
            ));

            // Clear the courses cache after updating a course
            AdminDataService.clearCache('courses');
        } catch (error) {
            console.error("Error updating course: ", error);
        }
    };

    const handleDelete = async (courseId) => {
        try {
            await deleteCourseFromFirestore(courseId);
            const updatedCourses = courses.filter(course => course.id !== courseId);
            setCourses(updatedCourses);

            // Clear relevant caches after deleting a course
            AdminDataService.clearCache('courses');
            AdminDataService.clearCache('courseGroups');
            AdminDataService.clearCache('courseData');
            
            // Clear localStorage order to remove deleted course
            const activeKey = `courseOrder:active:${user?.email || 'default'}`;
            const inactiveKey = `courseOrder:inactive:${user?.email || 'default'}`;
            localStorage.removeItem(activeKey);
            localStorage.removeItem(inactiveKey);
        } catch (error) {
            console.error("Error deleting course: ", error);
        }
    };

    const handleNavigateToGroupView = async (course) => {
        setLoading(true);
        try {
            // Fetch group data with events using our service
            const courseGroups = await AdminDataService.getCourseGroups(course.id, true);

            navigate(`/admin/home/group_viewer`, {
                state: {
                    groupData: courseGroups,
                    course,
                    from: 'home'
                }
            });
        } catch (error) {
            console.error("Error fetching groups for course:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <HomeHeader text="Dashboard" link="/admin/home" showPrivacyButton />
            <PageWrapper>
                <BackgroundBlock>
                    {loading ? (
                        <LoadingMessage>Loading courses...</LoadingMessage>
                    ) : (
                        <>
                            {/* Active Courses Section */}
                            <Section>
                                <SectionHeader>
                                    <ActiveIcon />
                                    <h2>Active Courses</h2>
                                    <Badge>{activeCourses.length}</Badge>
                                    {isProjectManager && (
                                        <SectionHeaderActions>
                                            <ReleaseNotesButton onClick={() => navigate("/maintenance/banner")}>
                                                <FiAlertCircle size={16} />
                                                <span>Maintenance Banner</span>
                                            </ReleaseNotesButton>
                                            <ReleaseNotesButton onClick={() => navigate("/maintenance/release-notes")}>
                                                <FiSend size={16} />
                                                <span>Update Notes</span>
                                            </ReleaseNotesButton>
                                            <ReleaseNotesButton onClick={() => navigate("/maintenance/issues")}>
                                                <FiAlertCircle size={16} />
                                                <span>Issue Reports</span>
                                            </ReleaseNotesButton>
                                        </SectionHeaderActions>
                                    )}
                                </SectionHeader>

                                <CoursesGrid>
                                    {activeCourses.map((course, index) => (
                                        <DraggableCourseCard
                                            key={course.id}
                                            data-draggable-card
                                            onDragOver={(e) => activeDragDrop.handleDragOver(e, index)}
                                            onDragLeave={activeDragDrop.handleDragLeave}
                                            onDrop={(e) => activeDragDrop.handleDrop(e, index)}
                                            onDragEnd={(e) => activeDragDrop.handleDragEnd(e)}
                                            $isDragging={activeDragDrop.draggedItem === index}
                                            $isDraggedOver={activeDragDrop.draggedOverIndex === index}
                                        >
                                            <DragHandle 
                                                className="drag-handle"
                                                draggable
                                                onDragStart={(e) => {
                                                    e.stopPropagation();
                                                    activeDragDrop.handleDragStart(e, index);
                                                }}
                                                onDragEnd={(e) => {
                                                    e.stopPropagation();
                                                    activeDragDrop.handleDragEnd(e);
                                                }}
                                            >
                                                <BsGripVertical />
                                            </DragHandle>
                                            <CourseCard
                                                course={course}
                                                onUpdate={handleUpdate}
                                                onDelete={handleDelete}
                                                onClick={() => handleNavigateToGroupView(course)}
                                            />
                                        </DraggableCourseCard>
                                    ))}

                                    {isAdmin && (
                                        addingCourse ? (
                                            <CourseForm
                                                course={newCourse}
                                                onInputChange={handleInputChange}
                                                onDateChange={handleDateChange}
                                                onSubmit={debouncedAddCourse}
                                                onCancel={handleCancel}
                                                submitButtonText="Add Course"
                                                isSubmitting={isSubmitting}
                                            />
                                        ) : (
                                            <AddCourseContainer onClick={() => setAddingCourse(true)}>
                                                <AddCourseText>Add Course +</AddCourseText>
                                            </AddCourseContainer>
                                        )
                                    )}
                                </CoursesGrid>
                            </Section>

                            {/* Inactive Courses Section - always shown below Active, even when empty */}
                            <Section>
                                <SectionHeader>
                                    <InactiveIcon />
                                    <h2>Inactive Courses</h2>
                                    <Badge $inactive>{inactiveCourses.length}</Badge>
                                </SectionHeader>
                                <CoursesGrid>
                                    {inactiveCourses.map((course, index) => (
                                        <DraggableCourseCard
                                            key={course.id}
                                            data-draggable-card
                                            onDragOver={(e) => inactiveDragDrop.handleDragOver(e, index)}
                                            onDragLeave={inactiveDragDrop.handleDragLeave}
                                            onDrop={(e) => inactiveDragDrop.handleDrop(e, index)}
                                            onDragEnd={(e) => inactiveDragDrop.handleDragEnd(e)}
                                            $isDragging={inactiveDragDrop.draggedItem === index}
                                            $isDraggedOver={inactiveDragDrop.draggedOverIndex === index}
                                        >
                                            <DragHandle 
                                                className="drag-handle"
                                                draggable
                                                onDragStart={(e) => {
                                                    e.stopPropagation();
                                                    inactiveDragDrop.handleDragStart(e, index);
                                                }}
                                                onDragEnd={(e) => {
                                                    e.stopPropagation();
                                                    inactiveDragDrop.handleDragEnd(e);
                                                }}
                                            >
                                                <BsGripVertical />
                                            </DragHandle>
                                            <CourseCard
                                                course={course}
                                                onUpdate={handleUpdate}
                                                onDelete={handleDelete}
                                                onClick={() => handleNavigateToGroupView(course)}
                                                inactive
                                            />
                                        </DraggableCourseCard>
                                    ))}
                                    {inactiveCourses.length === 0 && (
                                        <EmptyInactivePlaceholder>No inactive courses</EmptyInactivePlaceholder>
                                    )}
                                </CoursesGrid>
                            </Section>
                        </>
                    )}
                </BackgroundBlock>
            </PageWrapper>

            {showCancelConfirm && (
                <ConfirmDialog
                    title="Discard Changes?"
                    message="Are you sure you want to cancel? Unsaved changes will be lost."
                    confirmText="Yes, Discard"
                    cancelText="No, Go Back"
                    variant="primary"
                    onConfirm={confirmCancel}
                    onCancel={() => setShowCancelConfirm(false)}
                />
            )}
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
`;

const ReleaseNotesButton = styled.button`
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 14px;
    border: 1px solid #e9d5ff;
    border-radius: 8px;
    background: #f3e8ff;
    color: #7c3aed;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, transform 0.1s;

    &:hover {
        background: #e9d5ff;
        border-color: #d8b4fe;
    }

    &:active {
        transform: scale(0.95);
    }
`;

const AddCourseContainer = styled.div`
    background-color: #9333ea;
    border-radius: 10px;
    width: 300px;
    height: 266px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;

const AddCourseText = styled.div`
    font-size: 18px;
    font-weight: bold;
    color: #fff;
    user-select: none;
`;

const LoadingMessage = styled.div`
    width: 100%;
    padding: 20px;
    text-align: center;
    font-size: 16px;
    color: #666;
`;

const Section = styled.div`
    margin-bottom: 3rem;
    width: 100%;
    flex-shrink: 0;
`;

const EmptyInactivePlaceholder = styled.div`
    width: 100%;
    padding: 1.25rem 0;
    font-size: 0.9375rem;
    color: #6b7280;
    text-align: left;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    width: 100%;
    flex-wrap: wrap;
    min-height: 40px;

    h2 {
        font-size: 1.25rem;
        font-weight: bold;
    }
`;

const SectionHeaderActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 640px) {
        width: 100%;
        margin-left: 0;
        margin-top: 8px;
    }
`;

const Badge = styled.span`
    margin-left: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 50%;
    background-color: ${({ $inactive }) => $inactive ? '#e5e7eb' : '#ede9fe'};
    color: ${({ $inactive }) => $inactive ? '#6b7280' : '#7c3aed'};
`;

const CoursesGrid = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem;
`;

const ActiveIcon = styled(MdPlayCircleFilled)`
    color: #9333ea;
    width: 1.5rem;
    height: 1.5rem;
`;

const InactiveIcon = styled(MdArchive)`
    color: #6b7280;
    width: 1.5rem;
    height: 1.5rem;
`;

const DraggableCourseCard = styled.div`
    position: relative;
    transition: transform 0.2s ease, opacity 0.2s ease;
    opacity: ${({ $isDragging }) => $isDragging ? 0.5 : 1};
    transform: ${({ $isDraggedOver, $isDragging }) =>
        $isDraggedOver && !$isDragging ? 'translateY(-4px)' : 'translateY(0)'};

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: ${({ $isDraggedOver }) =>
            $isDraggedOver ? '2px dashed #9333ea' : '2px dashed transparent'};
        border-radius: 10px;
        pointer-events: none;
        transition: border-color 0.2s ease;
        z-index: 1;
    }
    
    &:hover .drag-handle {
        opacity: 1;
    }
`;

const DragHandle = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 10;
    color: #9333ea;
    opacity: 0;
    transition: opacity 0.2s ease;
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: auto;
    
    &:active {
        cursor: grabbing;
    }
    
    &:hover {
        background-color: rgba(255, 255, 255, 1);
        color: #7c3aed;
    }
    
    svg {
        width: 16px;
        height: 16px;
    }
`;


export default AdminHome;
